import {
  PrimaryButton,
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getAccountUsablePaymentMethod,
  getCurrentQuote,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  TKey,
  tPaymentMethods,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { PaymentMethod, PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { useCustomerAccount, usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../ProposalProvider'
import { DirectDebitSetup } from './DirectDebitSetup'

export interface PaymentDirectDebitProps {
  paymentMethod: PaymentMethod
  config: PaymentScheduleConfig
}

const tKey = TKey('components.payment-direct-debit')

export const PaymentDirectDebit: FunctionComponent<PaymentDirectDebitProps> = ({
  paymentMethod,
  config,
}) => {
  const { apiClient } = usePrincipal()

  const { t } = useWriskTranslation()

  const { product } = useProduct()
  const { proposal, dispatch } = useProposal()
  const { paymentMethods } = usePaymentMethods()
  const { customerAccount } = useCustomerAccount()

  const quote = getCurrentQuote(proposal)

  const usablePaymentMethod = getAccountUsablePaymentMethod(
    paymentMethods,
    PaymentMethodType.BACS_DEBIT,
    customerAccount.accountCode,
  )

  const premiums = useQuotePremiums(quote, product)

  const paymentType = getPaymentType(premiums)

  const onPay = useApiErrorHandlingAsyncCallback(async () => {
    dispatch({
      type: 'UPDATE_PROPOSAL',
      payload: await apiClient.bindProposal(proposal.proposalCode, {
        paymentMethodType: PaymentMethodType.BACS_DEBIT,
      }),
    })
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('title'))}</SectionTitle>
        <SectionDescription>
          {t(
            [
              tKey('description', config.type, paymentType),
              tKey('description', config.type),
              tKey('description'),
            ],
            {
              statement: t(tPaymentMethods('statement')),
            },
          )}
        </SectionDescription>
        <SectionContent>
          <DirectDebitSetup
            customerAccount={customerAccount}
            mandate={usablePaymentMethod}
            context={paymentMethod}
          />
        </SectionContent>
      </Section>
      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        disabled={!usablePaymentMethod}
        onClick={onPay.execute}
        loading={onPay.loading}
      >
        {t([
          tKey('action', config.type, paymentType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getPaymentType = (premiums: QuotePremiums): 'PAY' | 'BIND' =>
  premiums.totals.discounted > 0.01 ? 'PAY' : 'BIND'
